import { useRef } from "react";

import {
  Chart as ChartJS,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from "chart.js";

import { Chart } from "react-chartjs-2";
import ChartToolbar from "./ChartToolbar";


// Register the Chart.js components used by this chart.
ChartJS.register(
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
);


// Colours used for AC and DC charger data.
const AC_COLOUR = "#277a3e";
const DC_COLOUR = "#f5a000";


// Creates an array containing every hour from 0 to 23.
const hours = Array.from({ length: 24 }, (_, hour) => hour);


// Formats an hour into a readable time such as 08:00 or 17:00.
function formatHour(hour) {
  return `${String(hour).padStart(2, "0")}:00`;
}


function AverageEnergyByHourChart({
  data = [],
  chargerType = "Both"
}) {
  const chartRef = useRef(null);


  // Determine which charger types should be displayed.
  const showAC = chargerType === "Both" || chargerType === "AC";
  const showDC = chargerType === "Both" || chargerType === "DC";


  // Find the energy value for a particular charger type and hour.
  const findValue = (type, hour) => {
    const row = data.find(
      (item) =>
        item.chargerType === type &&
        Number(item.hour) === hour
    );

    return row ? Number(row.value) : 0;
  };


  // Build a value for every hour of the day for each charger type.
  const acValues = hours.map((hour) => findValue("AC", hour));
  const dcValues = hours.map((hour) => findValue("DC", hour));


  // Add datasets depending on the selected charger filter.
  const datasets = [];

  if (showAC) {
    datasets.push({
      type: "bar",
      label: "AC",
      data: acValues,
      backgroundColor: AC_COLOUR,
      borderColor: AC_COLOUR,
      borderWidth: 1
    });
  }

  if (showDC) {
    datasets.push({
      type: "bar",
      label: "DC",
      data: dcValues,
      backgroundColor: DC_COLOUR,
      borderColor: DC_COLOUR,
      borderWidth: 1
    });
  }


  const chartData = {
    labels: hours.map(formatHour),
    datasets
  };


  // Configure the chart layout, axes, legend and tooltips.
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: {
      mode: "index",
      intersect: false
    },
    scales: {
      x: {
        title: {
          display: true,
          text: "Time of Day"
        },
        ticks: {
          autoSkip: false,
          maxRotation: 90,
          minRotation: 90
        }
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Average Energy (kWh)"
        }
      }
    },
    plugins: {
      legend: {
        display: true,
        position: "right"
      },
      tooltip: {
        mode: "index",
        intersect: false
      }
    }
  };


  return (
    <article className="chart-card">
      <div className="chart-card-header">
        <div className="chart-card-title">
          <h2>Average Energy Over Time of Day</h2>

          <p>
            Average energy delivered by sessions beginning during each
            hour of the day.
          </p>
        </div>

        {/* Provides chart actions such as exporting/downloading */}
        <ChartToolbar
          chartRef={chartRef}
          title="Average Energy Over Time of Day"
          enableZoom={false}
        />
      </div>


      {/* Render the hourly average energy bar chart */}
      <div className="chart-canvas-container">
        <Chart
          ref={chartRef}
          type="bar"
          data={chartData}
          options={options}
        />
      </div>
    </article>
  );
}


export default AverageEnergyByHourChart;